import { ReactComponent as CloseLogo } from '@percent/cause-dashboard/common/assets/images/close.svg'
import { GrantAdvisoryBookingModal } from '@percent/cause-dashboard/common/components/grantAdvisoryBookingModal'
import {
  GRANT_ADVISORY_BOOKING_TRACKING,
  SMART_APPLY_GRANT_ADVISORY_EVENTS
} from '@percent/cause-dashboard/common/components/grantAdvisoryBookingModal/grantAdvisoryBooking.utils'
import { useGrantAdvisoryTrackingProperties } from '@percent/cause-dashboard/common/components/grantAdvisoryBookingModal/useGrantAdvisoryTrackingProperties'
import { useGrantAdvisoryBookingUrl } from '@percent/cause-dashboard/common/components/grantAdvisoryBookingModal/useGrantAdvisoryBookingUrl'
import { SmartApplyPartner } from '@percent/cause-dashboard/constants/smartApplyPartners'
import { GetManyValidationSubmission } from '@percent/cause-dashboard/services/applications/validationSubmission.types'
import { Box, Modal } from '@percent/lemonade'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ApplicationStatusPage, GrantsPage, SmartApplyPage, SecondSmartApplyPage } from '.'
import {
  POSTHOG_FEATURE_FLAGS,
  useCausesPortalAnalytics,
  useFeatureFlagsReady,
  useGfmBannerVariant,
  useIntroModalState,
  usePostHogFeatureFlag
} from '../../hooks'
import { InterestModal } from '../explicitSmartApply/interestModal/InterestModal'
import { SmartApplyRouter } from '../explicitSmartApply/SmartApplyRouter'
import { SmartApplyPartnerLogos } from '../explicitSmartApply/smartApplyPartnerLogos/SmartApplyPartnerLogos'
import styles from './IntroModals.module.scss'
import smartApplyPageStyles from './smartApplyPage/SmartApplyPage.module.scss'

type IntroModalsProps = {
  validationData: GetManyValidationSubmission | undefined
  hasCanvaApplication: boolean
  isSmartApplyBlocked: boolean
  isNonprofit: boolean
}

export function IntroModals({
  validationData,
  hasCanvaApplication,
  isSmartApplyBlocked,
  isNonprofit
}: IntroModalsProps) {
  const isPartnerApplyEnabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.CP_PARTNER_APPLY_ENABLED)
  const isSecondSmartApplyEnabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.CP_SECOND_SMART_APPLY)
  const isGrantAdvisoryAfterSmartApplyEnabled = usePostHogFeatureFlag(
    POSTHOG_FEATURE_FLAGS.CP_GRANT_ADVISORY_AFTER_SMART_APPLY
  )
  const areFlagsReady = useFeatureFlagsReady()
  const { isReady: isGfmModalReady, variant: gfmModalVariant } = useGfmBannerVariant('modal')
  const { track } = useCausesPortalAnalytics()
  const grantAdvisoryTrackingProperties = useGrantAdvisoryTrackingProperties()
  const grantAdvisoryBookingUrl = useGrantAdvisoryBookingUrl('smartApply', GRANT_ADVISORY_BOOKING_TRACKING.smartApply)

  const [isIntroModalOpen, setIsIntroModalOpen] = useState(false)
  const [isGrantAdvisoryOpen, setIsGrantAdvisoryOpen] = useState(false)
  const [interestModalDone, setInterestModalDone] = useState(false)

  const [hasViewedSmartApply, setHasViewedSmartApply] = useState(false)
  const [isStatusModalAvailable, setIsStatusModalAvailable] = useState(false)
  const [isSmartApplyAvailable, setIsSmartApplyAvailable] = useState(false)
  const [isGrantsPageAvailable, setIsGrantsPageAvailable] = useState(false)
  const [isSecondSmartApplyAvailable, setIsSecondSmartApplyAvailable] = useState(false)
  const [page, setPage] = useState(1)
  const [pagesCount, setPagesCount] = useState(0)
  const [modalWidth, setModalWidth] = useState(window.innerWidth > 1144 ? 1134 : window.innerWidth * 0.9)
  const [modalHeight, setModalHeight] = useState(window.innerHeight > 750 ? 740 : window.innerHeight * 0.9)
  const [selectedPartner, setSelectedPartner] = useState<SmartApplyPartner | null>(null)
  const [appliedPartners, setAppliedPartners] = useState<Set<SmartApplyPartner>>(new Set())
  const dismissCallbackRef = useRef<(() => void) | null>(null)

  const registerDismissCallback = useCallback((callback: (() => void) | null) => {
    dismissCallbackRef.current = callback
  }, [])

  const handleApplicationSuccess = () => {
    if (selectedPartner) {
      setAppliedPartners(prev => new Set(prev).add(selectedPartner))
      setSelectedPartner(null)
    }
  }

  const {
    shouldShowStatusCheck,
    recordStatusCheckAction,
    shouldShowGrants,
    recordGrantsAction,
    shouldShowSmartApply,
    recordSmartApplyAction,
    shouldShowSecondSmartApply,
    recordSecondSmartApplyAction,
    shouldShowGfmInterestModal
  } = useIntroModalState()

  const handleNextPage = () => {
    setPage(prev => prev + 1)
  }

  useEffect(() => {
    let count = 0

    if (
      validationData &&
      shouldShowStatusCheck &&
      (validationData.status === 'PENDING' || (validationData.processedAt && validationData.processedAt > '2025-09-20'))
      // NRG-64 added on 2025-09-25, don't want to show for older VSs
    ) {
      count++
      setIsStatusModalAvailable(true)
    }

    if (isPartnerApplyEnabled && shouldShowSmartApply && !isSmartApplyBlocked && isNonprofit) {
      count++
      setIsSmartApplyAvailable(true)
    }

    if (shouldShowGrants) {
      count++
      setIsGrantsPageAvailable(true)
    }

    if (
      isSecondSmartApplyEnabled &&
      isPartnerApplyEnabled &&
      shouldShowSecondSmartApply &&
      !isSmartApplyBlocked &&
      isNonprofit
    ) {
      count++
      setIsSecondSmartApplyAvailable(true)
    }

    if (count) {
      setIsIntroModalOpen(true)
    }

    setPagesCount(count)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSmartApplyBlocked, isNonprofit, isPartnerApplyEnabled, isSecondSmartApplyEnabled, shouldShowSecondSmartApply])

  useEffect(() => {
    const handleResize = () => {
      setModalWidth(window.innerWidth > 1144 ? 1134 : window.innerWidth * 0.95)
      setModalHeight(window.innerHeight > 750 ? 740 : window.innerHeight * 0.95)
    }

    window.addEventListener('resize', handleResize)
    handleResize()

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const allModals = [
    {
      name: 'applicationStatusPage',
      component: (
        <ApplicationStatusPage
          nextPage={pagesCount > 1 ? handleNextPage : undefined}
          validationData={validationData}
          pagesVisible={pagesCount > 1}
          page={page}
          pagesCount={pagesCount}
          height={modalHeight}
          width={modalWidth}
        />
      ),
      isAvailable: isStatusModalAvailable
    },
    {
      name: 'smartApplyPage',
      component: (
        <SmartApplyPage
          hasCanvaApplication={hasCanvaApplication}
          secondaryAction={pagesCount === page ? () => handleClose() : () => handleNextPage()}
          pagesVisible={pagesCount > 1}
          page={page}
          pagesCount={pagesCount}
          height={modalHeight}
          width={modalWidth}
          setSelectedPartner={setSelectedPartner}
          appliedPartners={appliedPartners}
          registerDismissCallback={registerDismissCallback}
        />
      ),
      isAvailable: isSmartApplyAvailable && !isSmartApplyBlocked && isNonprofit
    },
    {
      name: 'grantsPage',
      component: (
        <GrantsPage
          pagesVisible={pagesCount > 1}
          page={page}
          pagesCount={pagesCount}
          height={modalHeight}
          width={modalWidth}
        />
      ),
      isAvailable: isGrantsPageAvailable
    },
    {
      name: 'secondSmartApplyPage',
      component: (
        <div className={smartApplyPageStyles.modalCtn} style={{ height: modalHeight, width: '100%', maxWidth: modalWidth }}>
          <div className={`${smartApplyPageStyles.column} ${smartApplyPageStyles.leftColumn}`}>
            <div className={smartApplyPageStyles.leftCtn}>
              <SecondSmartApplyPage
                partners={['claude', 'meta', 'adobeExpress']}
                setSelectedPartner={setSelectedPartner}
                appliedPartners={appliedPartners}
                onApply={action => {
                  recordSecondSmartApplyAction(action)
                }}
                onDismiss={action => {
                  recordSecondSmartApplyAction(action)
                  closeIntroModals()
                }}
                registerDismissCallback={registerDismissCallback}
              />
            </div>
          </div>
          <div className={`${smartApplyPageStyles.column} ${smartApplyPageStyles.rightVariantColumn}`}>
            <SmartApplyPartnerLogos partners={['claude', 'meta', 'adobeExpress']} />
          </div>
        </div>
      ),
      isAvailable: isSecondSmartApplyAvailable
    }
  ]

  const availableModals = new Map(
    allModals.filter(({ isAvailable }) => isAvailable).map((modal, index) => [index + 1, modal])
  )

  const currentPageName = availableModals.get(page)?.name

  useEffect(() => {
    if (currentPageName === 'smartApplyPage' || currentPageName === 'secondSmartApplyPage') {
      setHasViewedSmartApply(true)
    }
  }, [currentPageName])

  const recordDismissalForCurrentPage = () => {
    if (availableModals.get(page)?.name === 'applicationStatusPage') {
      recordStatusCheckAction(new Date())
    }

    if (availableModals.get(page)?.name === 'smartApplyPage') {
      recordSmartApplyAction('dismissed')
    }

    if (availableModals.get(page)?.name === 'grantsPage') {
      recordGrantsAction('dismissed')
    }

    if (availableModals.get(page)?.name === 'secondSmartApplyPage') {
      recordSecondSmartApplyAction('dismissed')
    }
  }

  const closeIntroModals = () => {
    setIsIntroModalOpen(false)

    if (isGrantAdvisoryAfterSmartApplyEnabled && hasViewedSmartApply && grantAdvisoryBookingUrl) {
      track(SMART_APPLY_GRANT_ADVISORY_EVENTS.shown, grantAdvisoryTrackingProperties)
      setIsGrantAdvisoryOpen(true)
    }
  }

  const handleClose = () => {
    closeIntroModals()
    recordDismissalForCurrentPage()
  }

  const handleDismissPage = () => {
    dismissCallbackRef.current?.()
    recordDismissalForCurrentPage()

    if (page < availableModals.size) {
      handleNextPage()
    } else {
      closeIntroModals()
    }
  }

  if (isGrantAdvisoryOpen && grantAdvisoryBookingUrl) {
    return (
      <GrantAdvisoryBookingModal
        onClose={() => setIsGrantAdvisoryOpen(false)}
        entryPoint="smartApply"
        bookingUrl={grantAdvisoryBookingUrl}
        onScheduled={() => track(SMART_APPLY_GRANT_ADVISORY_EVENTS.scheduled, grantAdvisoryTrackingProperties)}
        analyticsProperties={grantAdvisoryTrackingProperties}
      />
    )
  }

  // Hold until the variant resolves, so Smart Apply cannot render and then be replaced.
  // useGfmBannerVariant falls back after BACKEND_FLAGS_TIMEOUT_MS.
  const isGfmModalPending = shouldShowGfmInterestModal && !interestModalDone

  if (isGfmModalPending && !isGfmModalReady) {
    return null
  }

  // `!selectedPartner`: never pull a user out of an application in progress
  if (isGfmModalPending && gfmModalVariant && !selectedPartner) {
    return (
      <InterestModal
        variant={gfmModalVariant}
        width={modalWidth}
        height={modalHeight}
        onDismiss={() => setInterestModalDone(true)}
      />
    )
  }

  if (!availableModals.size || !isIntroModalOpen) {
    return null
  }

  if (isSmartApplyAvailable && !areFlagsReady) {
    return null
  }

  const experimentVariantForRouter = currentPageName === 'secondSmartApplyPage' ? 'second_smart_apply' : undefined

  if (selectedPartner) {
    return (
      <SmartApplyRouter
        selectedPartner={selectedPartner}
        onClose={() => setSelectedPartner(null)}
        onSuccess={handleApplicationSuccess}
        experimentVariantOverride={experimentVariantForRouter}
      />
    )
  }

  return (
    <>
      <div className={styles.backgroundBlur}></div>
      <Modal
        width={modalWidth}
        height={modalHeight}
        overflowHidden
        open={isIntroModalOpen}
        onClose={handleClose}
        data-testid="offers-modals-container"
      >
        <Box className={styles.modalContainer}>
          <Box className={styles.modalHeader}>
            <button
              title="Close modal"
              id={styles.btnCloseDialog}
              onClick={handleDismissPage}
              type="button"
              data-testid="close-offers-modal-button"
            >
              <CloseLogo />
            </button>
          </Box>
          {availableModals.get(page)?.component}
        </Box>
      </Modal>
    </>
  )
}
